import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Nav } from 'react-bootstrap';
import { ChevronDown as FaChevronDown } from 'react-bootstrap-icons';
import { AdminNavGroupItem } from './adminNavItems';
import styles from './SideNavGroup.module.scss';

const isPathActive = (pathname: string, path: string) =>
  pathname === path || pathname.startsWith(`${path}/`);

interface SideNavGroupProps {
  item: AdminNavGroupItem;
  isSidebarExpanded: boolean;
  menuOpen: boolean;
  onToggleMenu: () => void;
  dropdownOpen: boolean;
  onToggleDropdown: () => void;
  onCloseDropdown: () => void;
  navIcons: Record<string, JSX.Element>;
  colors: {
    primary: string;
    onPrimary: string;
    hoverBg: string;
    secondaryText: string;
    bodyText: string;
  };
}

const SideNavGroup: React.FC<SideNavGroupProps> = ({
  item,
  isSidebarExpanded,
  menuOpen,
  onToggleMenu,
  dropdownOpen,
  onToggleDropdown,
  onCloseDropdown,
  navIcons,
  colors: { primary, onPrimary, hoverBg, secondaryText, bodyText },
}) => {
  const location = useLocation();
  const navigate = useNavigate();
  const dropdownRef = useRef<HTMLDivElement>(null);
  const [collapsedMenuPosition, setCollapsedMenuPosition] = useState({
    top: 0,
    left: 0,
  });

  useLayoutEffect(() => {
    if (!isSidebarExpanded && dropdownOpen && dropdownRef.current) {
      const updatePosition = () => {
        const rect = dropdownRef.current?.getBoundingClientRect();
        if (!rect) return;
        setCollapsedMenuPosition({
          top: rect.top,
          left: rect.right + 8,
        });
      };
      updatePosition();
      window.addEventListener('resize', updatePosition);
      window.addEventListener('scroll', updatePosition, true);
      return () => {
        window.removeEventListener('resize', updatePosition);
        window.removeEventListener('scroll', updatePosition, true);
      };
    }
  }, [dropdownOpen, isSidebarExpanded]);

  useEffect(() => {
    if (!isSidebarExpanded && dropdownOpen) {
      const handleClickOutside = (event: MouseEvent | TouchEvent) => {
        if (
          dropdownRef.current &&
          !dropdownRef.current.contains(event.target as Node)
        ) {
          onCloseDropdown();
        }
      };
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('touchstart', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
        document.removeEventListener('touchstart', handleClickOutside);
      };
    }
  }, [dropdownOpen, isSidebarExpanded, onCloseDropdown]);

  const hasActiveChild = item.children.some(child =>
    isPathActive(location.pathname, child.path)
  );

  // When the group is expanded, keep the parent quiet — the active child carries
  // the selection. When collapsed, highlight the parent so the section stays findable.
  const emphasizeParent = hasActiveChild && (!isSidebarExpanded || !menuOpen);

  const parentLinkStyle = (active: boolean): React.CSSProperties =>
    active ? { backgroundColor: primary, color: onPrimary } : {};

  return (
    <Nav.Item className="mb-1">
      {!isSidebarExpanded ? (
        <div ref={dropdownRef} style={{ position: 'relative' }}>
          <Nav.Link
            onClick={onToggleDropdown}
            className={`d-flex align-items-center justify-content-center rounded-3 p-2 ${
              emphasizeParent ? 'text-white' : 'text-dark hover-bg-light'
            }`}
            style={{
              ...parentLinkStyle(emphasizeParent),
              transition: 'all 0.2s ease',
              cursor: 'pointer',
              fontSize: '14px',
              fontWeight: emphasizeParent || hasActiveChild ? '600' : '500',
            }}
            title={item.text}
            onMouseEnter={e => {
              if (!emphasizeParent) {
                e.currentTarget.style.backgroundColor = hoverBg;
              }
            }}
            onMouseLeave={e => {
              if (!emphasizeParent) {
                e.currentTarget.style.backgroundColor = 'transparent';
              }
            }}
          >
            <span
              className="fs-5"
              style={{
                color: emphasizeParent ? onPrimary : secondaryText,
                transition: 'color 0.2s ease',
              }}
            >
              {navIcons[item.icon]}
            </span>
          </Nav.Link>
          {dropdownOpen && (
            <div
              style={{
                position: 'fixed',
                top: collapsedMenuPosition.top,
                left: collapsedMenuPosition.left,
                backgroundColor: 'var(--bs-body-bg)',
                borderRadius: '10px',
                boxShadow: 'none',
                minWidth: '180px',
                zIndex: 1100,
                border: '1px solid var(--bs-border-color)',
                overflow: 'hidden',
              }}
            >
              {item.children.map((child, childIndex) => {
                const isActive = isPathActive(location.pathname, child.path);
                const isLast = childIndex === item.children.length - 1;
                return (
                  <div
                    key={child.path}
                    onClick={() => {
                      navigate(child.path);
                      onCloseDropdown();
                    }}
                    className={`${styles.collapsedChild} ${
                      isActive ? styles.collapsedChildActive : ''
                    }`}
                    style={{
                      borderBottom: isLast
                        ? 'none'
                        : '1px solid var(--bs-border-color)',
                    }}
                  >
                    <span className={styles.childIcon}>
                      {navIcons[child.icon]}
                    </span>
                    <span className={styles.childLabel}>{child.text}</span>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      ) : (
        <>
          <Nav.Link
            onClick={onToggleMenu}
            className={`d-flex align-items-center rounded-3 p-2 ${
              emphasizeParent ? 'text-white' : 'text-dark hover-bg-light'
            }`}
            style={{
              ...parentLinkStyle(emphasizeParent),
              transition: 'all 0.2s ease',
              cursor: 'pointer',
              fontSize: '14px',
              fontWeight: emphasizeParent || hasActiveChild ? '600' : '500',
            }}
            title={item.text}
            onMouseEnter={e => {
              if (!emphasizeParent) {
                e.currentTarget.style.backgroundColor = hoverBg;
              }
            }}
            onMouseLeave={e => {
              if (!emphasizeParent) {
                e.currentTarget.style.backgroundColor = 'transparent';
              }
            }}
          >
            <span
              className="me-2 fs-5"
              style={{
                color: emphasizeParent ? onPrimary : secondaryText,
                transition: 'color 0.2s ease',
              }}
            >
              {navIcons[item.icon]}
            </span>
            <span
              className="fw-medium flex-grow-1"
              style={{
                color: emphasizeParent ? onPrimary : bodyText,
                transition: 'color 0.2s ease',
              }}
            >
              {item.text}
            </span>
            <span
              style={{
                color: emphasizeParent ? onPrimary : secondaryText,
                transition: 'transform 0.2s ease',
                transform: menuOpen ? 'rotate(0deg)' : 'rotate(-90deg)',
              }}
            >
              <FaChevronDown size={12} />
            </span>
          </Nav.Link>
          {menuOpen && (
            <div className={styles.childList}>
              {item.children.map(child => {
                const isActive = isPathActive(location.pathname, child.path);
                return (
                  <Nav.Link
                    key={child.path}
                    as="button"
                    type="button"
                    onClick={() => navigate(child.path)}
                    className={`${styles.childLink} ${
                      isActive ? styles.childLinkActive : ''
                    }`}
                  >
                    <span className={styles.childIcon}>
                      {navIcons[child.icon]}
                    </span>
                    <span className={styles.childLabel}>{child.text}</span>
                  </Nav.Link>
                );
              })}
            </div>
          )}
        </>
      )}
    </Nav.Item>
  );
};

export default SideNavGroup;
